import { Fragment, useMemo, useState } from 'react';
import useIsMobile from '../auth/useIsMobile';

/**
 * Lightweight data table with client-side search, sortable columns, and an
 * optional export button. Keeps column rendering declarative so each module
 * doesn't re-roll a <table>.
 *
 * columns: [
 *   {
 *     key,          // field on row OR a synthetic id
 *     label,        // header text
 *     render?,      // (row) => ReactNode for custom cell markup
 *     filter?,      // boolean — include in search? default true
 *     sortable?,    // boolean — let users sort by this column? default true
 *                   //   (keys starting with '_' are always non-sortable)
 *     sortValue?,   // (row) => primitive, for columns where row[key] isn't
 *                   //   the right thing to compare (arrays, objects, etc.)
 *     width?,
 *   }
 * ]
 *
 * defaultSort?: { key, dir: 'asc' | 'desc' }
 *
 * groupBy?: {
 *   getKey:   (row) => string         // which group does this row belong to?
 *   getLabel?: (key) => string        // banner text for the group (defaults to the key)
 *   order?:    string[]               // group display order; unknown keys land at the end
 *   collapsible?: boolean             // groups start collapsed; click a banner to expand
 * }
 *   When provided, the table renders a banner row above each non-empty
 *   group with the label and a count. Sort still applies — but only
 *   reorders rows within their own group, so group sections stay
 *   contiguous regardless of which column the user sorted by.
 *
 * subRender?: (row) => ReactNode | null
 *   Optional per-row sub-content (e.g. a free-form Notes block) that
 *   renders as a full-width second <tr> directly under the main row.
 *   Returning null/undefined/empty string skips the sub-row entirely,
 *   so rows without sub-content stay tight. Useful for surfacing
 *   long-form fields without bloating column widths.
 */
export default function DataTable({
  rows,
  columns,
  searchable = true,
  exportButton,
  emptyMessage = 'No records yet.',
  onRowClick,
  toolbarExtras,
  defaultSort = null,
  groupBy = null,
  subRender = null,
  // When true, on phone/tablet widths the full list is replaced by a
  // search box — rows only appear once the user types something. Keeps
  // big data tables (Plants, Suppliers, Parts, Team) from dumping the
  // whole list on a small screen.
  mobileSearchOnly = false,
  // Placeholder for the big mobile search box, e.g. "Search Plants".
  searchLabel = 'Search…',
}) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(defaultSort);
  // Collapsible groups (groupBy.collapsible): which banners are open. While
  // searching, every group with a match opens so results aren't hidden.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isMobile = useIsMobile();
  // On mobile + mobileSearchOnly, hold back the table until there's a query.
  const hideUntilSearch = mobileSearchOnly && isMobile && !q.trim();

  // Visible columns are what we render in the head/body. Hidden columns
  // (e.g. flattened-text fields like `contactsText`) stay in the column
  // list so search still indexes them, but they don't take up screen
  // real estate. Pass `hidden: true` on a column to opt in.
  // Hidden columns never render. `mobileHidden` columns drop out on phone
  // widths only — they still index for search (filtering runs over the full
  // `columns` list, not this one) and still show on the desktop table and on
  // the record's detail page.
  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hidden && !(isMobile && c.mobileHidden)),
    [columns, isMobile],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    const searchCols = columns.filter((c) => c.filter !== false);
    return rows.filter((row) =>
      searchCols.some((c) => {
        const v = row[c.key];
        if (v == null) return false;
        return String(v).toLowerCase().includes(needle);
      }),
    );
  }, [rows, q, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const extract = col.sortValue || ((row) => row[col.key]);
    const copy = [...filtered];
    copy.sort((a, b) => compareValues(extract(a), extract(b)));
    if (sort.dir === 'desc') copy.reverse();
    return copy;
  }, [filtered, sort, columns]);

  const isSortable = (c) =>
    c.sortable !== false && typeof c.key === 'string' && !c.key.startsWith('_');

  const toggleSort = (col) => {
    if (!isSortable(col)) return;
    setSort((cur) => {
      if (!cur || cur.key !== col.key) return { key: col.key, dir: 'asc' };
      if (cur.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null; // third click clears
    });
  };

  const showBigSearch = mobileSearchOnly && isMobile;

  return (
    <>
      {showBigSearch && (
        <MobileSearchBox value={q} onChange={setQ} label={searchLabel} />
      )}
      {!hideUntilSearch && (
      <div className="table-wrapper">
        {!showBigSearch && (searchable || exportButton || toolbarExtras) && (
          <div className="table-toolbar">
            {searchable && (
              <input
                type="search"
                placeholder={searchLabel}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            )}
            <div style={{ flex: 1 }} />
            {toolbarExtras}
            {exportButton}
          </div>
        )}
      <table className="data">
        <thead>
          <tr>
            {visibleColumns.map((c) => {
              const active = sort?.key === c.key;
              const sortable = isSortable(c);
              return (
                <th
                  key={c.key}
                  style={{
                    ...(c.width ? { width: c.width } : {}),
                    // `compact` columns use tighter horizontal padding so
                    // short-value columns (e.g. State / Country) don't reserve
                    // more width than they need.
                    ...(c.compact
                      ? { paddingLeft: 8, paddingRight: 8 }
                      : {}),
                    ...(sortable
                      ? { cursor: 'pointer', userSelect: 'none' }
                      : {}),
                  }}
                  onClick={sortable ? () => toggleSort(c) : undefined}
                  title={sortable ? 'Click to sort' : undefined}
                >
                  {c.label}
                  {sortable && (
                    <span
                      aria-hidden="true"
                      style={{
                        marginLeft: 6,
                        opacity: active ? 1 : 0.25,
                        fontSize: 10,
                      }}
                    >
                      {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▲▼'}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={visibleColumns.length} className="empty">
                {emptyMessage}
              </td>
            </tr>
          )}
          {sorted.length > 0 && groupBy ? (
            // Group mode — bucket rows by groupBy.getKey, then render
            // each non-empty group preceded by a banner row. Order
            // follows groupBy.order when provided; unknown keys land
            // at the end in first-seen order.
            renderGroups(sorted, visibleColumns, groupBy, onRowClick, subRender, {
              openGroups,
              toggleGroup,
              forceOpen: Boolean(q.trim()),
            })
          ) : (
            sorted.map((row, idx) => {
              const stripe = idx % 2 === 1 ? '#f7f8fa' : '#ffffff';
              const sub = subRender ? subRender(row) : null;
              return (
                <Fragment key={row.id}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{
                      background: stripe,
                      ...(onRowClick ? { cursor: 'pointer' } : {}),
                    }}
                  >
                    {visibleColumns.map((c) => (
                      <td
                        key={c.key}
                        style={
                          c.compact
                            ? { paddingLeft: 8, paddingRight: 8 }
                            : undefined
                        }
                      >
                        {c.render ? c.render(row) : row[c.key] ?? ''}
                      </td>
                    ))}
                  </tr>
                  {sub ? (
                    <tr style={{ background: stripe }}>
                      <td
                        colSpan={visibleColumns.length}
                        style={{
                          paddingTop: 0,
                          paddingBottom: 12,
                        }}
                      >
                        {sub}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      </div>
      )}
    </>
  );
}

/**
 * Render rows split into labeled groups. Each non-empty group gets a
 * banner row above it (brand-orange soft, matches the role-section
 * banners on the Proxemics Team page). Groups appear in `order` first;
 * any unknown group keys hit at the end in first-seen order. Row
 * striping resets per-group so each section reads cleanly on its own.
 */
function renderGroups(rows, columns, groupBy, onRowClick, subRender, collapse = {}) {
  const { getKey, getLabel, order = [], collapsible = false } = groupBy || {};
  const { openGroups, toggleGroup, forceOpen } = collapse;
  if (typeof getKey !== 'function') return null;
  const buckets = new Map();
  // Pre-seed declared order so empty buckets don't accidentally get
  // shuffled to the end if no rows fall into them.
  for (const k of order) {
    if (!buckets.has(k)) buckets.set(k, []);
  }
  for (const row of rows) {
    const key = getKey(row) || 'Other';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  }
  // Drop empty buckets (declared but unused) so we don't render
  // empty section banners.
  const entries = [...buckets.entries()].filter(([, list]) => list.length > 0);
  return entries.map(([key, list]) => {
    const isOpen = !collapsible || forceOpen || (openGroups && openGroups.has(key));
    return (
    <Fragment key={key}>
      <tr
        onClick={collapsible && !forceOpen ? () => toggleGroup(key) : undefined}
        style={collapsible && !forceOpen ? { cursor: 'pointer' } : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
      >
        <td
          colSpan={columns.length}
          style={{
            background: 'var(--brand-primary-soft)',
            padding: '10px 12px',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--brand-primary-dark)',
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
          }}
        >
          {collapsible && (
            <span aria-hidden="true" style={{ display: 'inline-block', width: 16 }}>
              {isOpen ? '▾' : '▸'}
            </span>
          )}
          {getLabel ? getLabel(key) : key}
          <span
            style={{
              fontWeight: 400,
              marginLeft: 8,
              textTransform: 'none',
            }}
          >
            ({list.length})
          </span>
        </td>
      </tr>
      {isOpen && list.map((row, idx) => {
        const stripe = idx % 2 === 1 ? '#f7f8fa' : '#ffffff';
        const sub = subRender ? subRender(row) : null;
        return (
          <Fragment key={row.id}>
            <tr
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                background: stripe,
                ...(onRowClick ? { cursor: 'pointer' } : {}),
              }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={
                    c.compact ? { paddingLeft: 8, paddingRight: 8 } : undefined
                  }
                >
                  {c.render ? c.render(row) : row[c.key] ?? ''}
                </td>
              ))}
            </tr>
            {sub ? (
              <tr style={{ background: stripe }}>
                <td
                  colSpan={columns.length}
                  style={{ paddingTop: 0, paddingBottom: 12 }}
                >
                  {sub}
                </td>
              </tr>
            ) : null}
          </Fragment>
        );
      })}
    </Fragment>
    );
  });
}

/**
 * Compare two values for sorting. Handles:
 *   - nullish values (sorted to the bottom regardless of direction)
 *   - numbers (including numeric strings, sorted numerically)
 *   - ISO-date-looking strings (sorted chronologically)
 *   - everything else (localeCompare with numeric/base options)
 *
 * The caller flips direction by reversing the sorted array — this keeps the
 * comparator itself direction-agnostic and makes nullish handling correct in
 * both asc and desc ("missing" should always sink, not flip to the top).
 */
/**
 * Hook + <th> companion for tables that don't use <DataTable> directly
 * but still want the same sortable-column UX (Plant Detail's
 * Suppliers table, the Billing dashboard's rollup tables, etc.).
 *
 * Pass a `sortConfig` map of `{ columnKey: (row) => primitive }` so
 * the comparator works for derived / composite cells. Three-state
 * cycle on click — asc → desc → cleared.
 */
export function useSortedRows(rows, sortConfig, defaultSort = null) {
  const [sort, setSort] = useState(defaultSort);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const extract = sortConfig[sort.key];
    if (!extract) return rows;
    const copy = [...rows];
    copy.sort((a, b) => compareValues(extract(a), extract(b)));
    if (sort.dir === 'desc') copy.reverse();
    return copy;
  }, [rows, sort, sortConfig]);
  const onSort = (key) =>
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: 'asc' };
      if (cur.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  return [sorted, sort, onSort];
}

/**
 * <th> cell that renders the column label plus a small sort arrow.
 * Inactive: faded ▲▼. Active: solid ▲ or ▼. Click cycles the state
 * via the parent-provided onSort. Pair with useSortedRows above.
 */
export function SortableTh({
  children,
  sortKey,
  currentSort,
  onSort,
  align,
  width,
}) {
  const active = currentSort?.key === sortKey;
  const arrow = active ? (currentSort.dir === 'asc' ? '▲' : '▼') : '▲▼';
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        ...(align ? { textAlign: align } : {}),
        ...(width ? { width } : {}),
      }}
    >
      {children}
      <span
        aria-hidden="true"
        style={{
          marginLeft: 6,
          opacity: active ? 1 : 0.25,
          fontSize: 10,
        }}
      >
        {arrow}
      </span>
    </th>
  );
}

export function compareValues(a, b) {
  const aMissing = a == null || a === '';
  const bMissing = b == null || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  // Native numbers
  if (typeof a === 'number' && typeof b === 'number') return a - b;

  // Numeric strings (money, hours, counts stored as strings)
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && a !== '' && b !== '') {
    return na - nb;
  }

  // ISO date strings — YYYY-MM-DD or with time component
  if (typeof a === 'string' && typeof b === 'string' && /^\d{4}-\d{2}-\d{2}/.test(a) && /^\d{4}-\d{2}-\d{2}/.test(b)) {
    return Date.parse(a) - Date.parse(b);
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Big, obvious orange search box used on mobile in place of a data list.
 * A magnifier on the left, the section label as the placeholder
 * ("Search Plants" / "Search Suppliers" / "Search Team Members"), and an
 * arrow on the right so it clearly reads as a "search and go" field.
 * Exported so pages with their own bespoke tables (e.g. Team) reuse it.
 */
export function MobileSearchBox({ value, onChange, label = 'Search…' }) {
  return (
    <div className="mobile-search-box">
      <svg className="msb-icon" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <line
          x1="16.5"
          y1="16.5"
          x2="21"
          y2="21"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="search"
        placeholder={label}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <svg className="msb-arrow" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5 12h13M13 6l6 6-6 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
